import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { filter, take, tap } from "rxjs";
import { singleton } from "tsyringe";

import SdkAnalyticsEventsFactory from "./analytics/SdkAnalyticsEventsFactory";
import CommonSdk from "./common_sdk/CommonSdk";
import { SDK_MODULES } from "./constants";
import { SdkDynamicModulesManager } from "./dynamic_modules/SdkDynamicModulesManager";
import SdkEvent from "./events/SdkEvent";
import { SdkEventInterface } from "./events/SdkEventInterface";
import { HookDefinition, HookFunction } from "./hooks/HookDefinition";
import { HooksManager } from "./hooks/HooksManager";
import { SdkRegisteredModuleInterface } from "./SdkRegisteredModuleInterface";
import { SdkInitOptions } from "./types";

@singleton()
class Sdk {
    private readonly _emitter: EventTarget;

    public readonly common?: CommonSdk;

    constructor(
        private readonly _analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        private readonly _baseModule: BaseModule,
        private readonly _sdkAnalyticsEventsFactory: SdkAnalyticsEventsFactory,
        private readonly _hooksManager: HooksManager,
        private readonly _sdkDynamicModulesManager: SdkDynamicModulesManager,
        private readonly _initApp?: (options: SdkInitOptions) => void
    ) {
        this._emitter = new EventTarget();
    }

    public init(options: SdkInitOptions) {
        if (this._initApp) {
            this._initApp(options);
        }
    }

    public on(type: string, callback: (event: Event) => void) {
        this._emitter.addEventListener(type, callback);
        this._analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this._sdkAnalyticsEventsFactory.createEventSubscribed(type)
        );
    }

    public off(type: string, callback: (event: Event) => void) {
        this._emitter.removeEventListener(type, callback);
        this._analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this._sdkAnalyticsEventsFactory.createEventUnsubscribed(type)
        );
    }

    public runIfModuleReady(
        moduleName: Exclude<SDK_MODULES, SDK_MODULES.COMMON>,
        callback: () => void
    ): void {
        this._sdkDynamicModulesManager.fetch(moduleName).catch((error) => {
            console.error(`Failed to fetch module ${moduleName}:`, error);
        });
        this._baseModule.exports.modulesStatusesProvider
            .selectModuleStatus(moduleName)
            .pipe(
                filter((status) => status === MODULE_STATUS.INITIALIZED),
                take(1),
                tap(callback)
            )
            .subscribe();
    }

    public dispatch(event: SdkEventInterface) {
        this._emitter.dispatchEvent(new SdkEvent(event.type, event));
    }

    public registerModule(
        moduleName: SDK_MODULES,
        module: SdkRegisteredModuleInterface
    ) {
        module.register(this);

        this._baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            moduleName,
            MODULE_STATUS.INITIALIZED
        );

        this._analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this._sdkAnalyticsEventsFactory.createSdkModuleRegistered(
                moduleName
            )
        );
    }

    public registerHook<TInput, TResult>(
        definition: HookDefinition<TInput, TResult>,
        hookFunction: HookFunction<TInput, TResult>
    ): void {
        this._hooksManager.registerHook(definition, hookFunction);
    }
}

export default Sdk;
